/**
 * src/utils/stellarStrKey.ts
 *
 * Deciding whether a run of capital letters is a Stellar address.
 *
 * The chat needs this to render an address as an address and to keep
 * speech from spelling one out. Both jobs share one failure mode, and it
 * is the reason this file exists rather than a regex: a test for "starts
 * with C and is 56 characters" says yes to `CONTRACT_ADDRESS_GOES_HERE`
 * padded to length, and says yes to a real address with one character
 * typed wrong. Displaying the first as a copyable identifier is merely
 * silly. Silently accepting the second is how somebody copies an address
 * that funds nothing.
 *
 * So this is the actual StrKey check — base32 decode, version byte,
 * CRC16-XModem over the payload — which is what makes the negative cases
 * negative. `XLM`, `USDC` and `BURJ KHALIFA` are not addresses because
 * they do not decode, not because they are short.
 *
 * WHY NOT THE SDK. `@stellar/stellar-sdk` exports exactly this as
 * `StrKey` and the server uses it. It cannot be imported here. The SDK
 * needs `TextEncoder`, jsdom does not provide one, and that is already
 * why `spatialParticipant.test.ts` carries `@jest-environment node`. A
 * module rendered inside a React chat bubble has to work under jsdom, so
 * importing the SDK would mean no component test of the chat could run.
 * Pulling the whole SDK into the browser bundle to validate a string is
 * the other half of the argument.
 *
 * Duplicated logic drifts, so it is pinned rather than trusted:
 * `stellarStrKey.test.ts` runs under the node environment, imports the
 * real `StrKey`, and asserts the two agree across a corpus. If this file
 * ever disagrees with the SDK, that test fails rather than the chat
 * quietly misreading an address.
 */

/** What a StrKey's leading character says it is. */
export type StrKeyKind =
  | 'contract'
  | 'account'
  | 'muxed'
  | 'seed'
  | 'preAuthTx'
  | 'sha256Hash';

/**
 * Version bytes, from SEP-23.
 *
 * The byte is the first five bits of the base32, which is why each kind
 * has a fixed leading letter. Kept as the byte rather than the letter
 * because the letter is a consequence and the byte is the definition.
 */
const VERSION_BYTE: Readonly<Record<StrKeyKind, number>> = Object.freeze({
  account: 6 << 3, // G
  seed: 18 << 3, // S
  preAuthTx: 19 << 3, // T
  sha256Hash: 23 << 3, // X
  muxed: 12 << 3, // M
  contract: 2 << 3, // C
});

const BY_VERSION_BYTE: ReadonlyMap<number, StrKeyKind> = new Map(
  (Object.keys(VERSION_BYTE) as StrKeyKind[]).map((kind) => [VERSION_BYTE[kind], kind]),
);

/**
 * Payload lengths in bytes, excluding version byte and checksum.
 *
 * A muxed account is 40 rather than 32: it carries the 8-byte subaccount
 * id after the key. Checking this is what stops a 56-character `M` string
 * from passing as one, since a real muxed address is 69 characters.
 */
const PAYLOAD_BYTES: Readonly<Record<StrKeyKind, number>> = Object.freeze({
  account: 32,
  seed: 32,
  preAuthTx: 32,
  sha256Hash: 32,
  muxed: 40,
  contract: 32,
});

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/** Reverse lookup, built once. `-1` for anything outside the alphabet. */
const BASE32_VALUE: Int8Array = (() => {
  const table = new Int8Array(128).fill(-1);
  for (let i = 0; i < BASE32_ALPHABET.length; i += 1) {
    table[BASE32_ALPHABET.charCodeAt(i)] = i;
  }
  return table;
})();

/**
 * Decode unpadded base32, or fail.
 *
 * Case-sensitive on purpose. StrKeys are canonically uppercase, and
 * accepting lowercase would make `caaq…` an address in the chat while the
 * SDK rejects it — a disagreement the pinning test would catch, so it is
 * simply not created.
 *
 * The leftover-bits check at the end is load-bearing. Base32 encodes 5
 * bits per character, so a byte count that is not a whole number of
 * characters leaves a remainder; a string of valid characters at the
 * wrong length would otherwise decode to something plausible.
 */
function _decodeBase32(value: string): Uint8Array | null {
  const bits = value.length * 5;
  const bytes = Math.floor(bits / 8);
  // Whatever is left over has to be padding, and padding has to be zero.
  if (bits - bytes * 8 >= 5) return null;

  const out = new Uint8Array(bytes);
  let accumulator = 0;
  let accumulated = 0;
  let written = 0;

  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    const digit = code < 128 ? BASE32_VALUE[code] : -1;
    if (digit < 0) return null;

    accumulator = (accumulator << 5) | digit;
    accumulated += 5;

    if (accumulated >= 8) {
      accumulated -= 8;
      out[written] = (accumulator >> accumulated) & 0xff;
      written += 1;
    }
  }

  // Trailing bits must be zero, or this is not a canonical encoding.
  if (accumulated > 0 && (accumulator & ((1 << accumulated) - 1)) !== 0) return null;

  return out;
}

/**
 * CRC16-XModem, which is the checksum StrKey uses.
 *
 * Polynomial 0x1021, zero initial value, no final xor. Written out
 * bitwise rather than table-driven: this runs a handful of times per chat
 * message, and a 256-entry table would be more code to be wrong in.
 */
function _crc16(bytes: Uint8Array): number {
  let crc = 0x0000;
  for (let i = 0; i < bytes.length; i += 1) {
    crc ^= bytes[i] << 8;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc & 0x8000) !== 0 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc & 0xffff;
}

/**
 * What kind of StrKey this is, or null.
 *
 * Total: any input at all, including things that are not strings, yields
 * null rather than throwing. Callers are renderers and speech filters,
 * and neither has anything useful to do with an exception.
 */
export function strKeyKind(value: unknown): StrKeyKind | null {
  if (typeof value !== 'string') return null;
  // Cheap gate before the decode. The shortest StrKey is 56 characters
  // and the longest is 69, so anything outside that cannot be one.
  if (value.length < 56 || value.length > 69) return null;

  const decoded = _decodeBase32(value);
  // Version byte + payload + 2-byte checksum, at minimum.
  if (!decoded || decoded.length < 4) return null;

  const kind = BY_VERSION_BYTE.get(decoded[0]);
  if (kind === undefined) return null;

  const payload = decoded.length - 3;
  if (payload !== PAYLOAD_BYTES[kind]) return null;

  const expected = _crc16(decoded.subarray(0, decoded.length - 2));
  // Little-endian, per SEP-23.
  const found = decoded[decoded.length - 2] | (decoded[decoded.length - 1] << 8);
  if (expected !== found) return null;

  return kind;
}

/** Whether this is any valid StrKey. */
export function isStrKey(value: unknown): boolean {
  return strKeyKind(value) !== null;
}

/** Whether this is a Soroban contract address. */
export function isContractId(value: unknown): boolean {
  return strKeyKind(value) === 'contract';
}

/**
 * Whether this is something a person holds — an account or a muxed one.
 *
 * Seeds are deliberately not included. A seed is not an account you show
 * somebody, and the callers that ask this question are asking so they can
 * display the value.
 */
export function isAccountId(value: unknown): boolean {
  const kind = strKeyKind(value);
  return kind === 'account' || kind === 'muxed';
}

/**
 * Whether this is a secret seed.
 *
 * Exists so the speech layer can drop one unconditionally. A seed should
 * never be in a chat message; if one ever is, reading it aloud is the
 * worst available outcome, so it is recognised rather than assumed absent.
 */
export function isSecretSeed(value: unknown): boolean {
  return strKeyKind(value) === 'seed';
}

/** A 64-character hex string, which is how transaction hashes arrive. */
const HEX_64 = /^[0-9a-f]{64}$/i;

/** Whether this looks like a transaction hash. */
export function isTransactionHash(value: unknown): boolean {
  return typeof value === 'string' && HEX_64.test(value);
}

/**
 * Shorten an identifier for display, keeping both ends.
 *
 * Both ends rather than a prefix, because the head of a contract address
 * is the least distinguishing part of it — two contracts deployed from
 * the same source share several leading characters, and a visitor
 * comparing what is on screen with what is in their wallet is comparing
 * the tail. The full value stays reachable on the element.
 */
export function truncateIdentifier(value: string, head = 6, tail = 6): string {
  const raw = String(value ?? '');
  // Nothing is gained by truncating to a length near the original, and an
  // ellipsis in the middle of something short reads as damage.
  if (raw.length <= head + tail + 3) return raw;
  return `${raw.slice(0, head)}…${raw.slice(-tail)}`;
}
